import React, { useMemo, useRef, useState } from 'react';
import { WorkflowStageEntity } from '../interface';

/* =====================================================================
   RouteFlowDiagram Component
   =====================================================================
   Renders a workflow route (e.g. RM -> [ALUCUT -> ASSEMBLY || DHO] -> PAINT -> RFD)
   as an easy-to-read, clean SVG diagram.

   HOW IT WORKS (Simple 3 Steps):
   1. Place each work center box on a grid (X = left to right, Y = lanes)
   2. Draw straight lines between steps in the same leg (ALUCUT -> ASSEMBLY)
   3. Draw smooth curves when splitting ({) or merging (}) parallel legs
   ===================================================================== */

// -------------------------------------------------------------
// 1. DIMENSIONS & COLORS (Easy to adjust anytime)
// -------------------------------------------------------------
/** 'default' is the compact settings / route builder view; 'large' is the floor-facing tracking view that carries a date and name under every completed step */
export type DiagramSize = 'default' | 'large';

interface DiagramDimensions {
    boxWidth: number;            // MINIMUM width of each work center box
    maxBoxWidth: number;         // cap — codes that still don't fit get truncated with "…"
    codeCharWidth: number;       // approx width of one code character at codeFontSize, weight 600
    subLabelCharWidth: number;   // approx width of one sub-label character at subLabelFontSize
    subLabelLineHeight: number;  // vertical pitch between sub-label lines inside the fixed-height box
    textSidePadding: number;     // left+right breathing room between the code text and the box edges
    boxHeight: number;           // Height of each work center box
    horizontalGap: number;       // Distance between steps horizontally
    rowGap: number;              // Distance between parallel legs vertically
    padding: number;             // Outer padding around diagram
    cornerRadius: number;        // Box corner radius
    codeFontSize: number;        // Work center code text
    positionFontSize: number;    // Route's own "2.1.1" sub-label
    subLabelFontSize: number;    // Caller-supplied sub-label (date, name)
    subLabelOpacity: number;     // Caller-supplied sub-label opacity
}

const DIAGRAM_SIZES: Record<DiagramSize, DiagramDimensions> = {
    default: {
        boxWidth: 104, maxBoxWidth: 200, codeCharWidth: 7.6, subLabelCharWidth: 5, subLabelLineHeight: 11, textSidePadding: 18,
        boxHeight: 46, horizontalGap: 34, rowGap: 72, padding: 20, cornerRadius: 10,
        codeFontSize: 12.5, positionFontSize: 9.5, subLabelFontSize: 9, subLabelOpacity: 0.6,
    },
    large: {
        boxWidth: 124, maxBoxWidth: 224, codeCharWidth: 7.9, subLabelCharWidth: 5.5, subLabelLineHeight: 12, textSidePadding: 20,
        boxHeight: 56, horizontalGap: 36, rowGap: 82, padding: 20, cornerRadius: 10,
        codeFontSize: 13, positionFontSize: 10.5, subLabelFontSize: 10, subLabelOpacity: 0.8,
    },
};

const LINE_COLOR = '#B0BAC7'; // Crisp neutral line color

export type WorkCenterProgressStatus = 'current' | 'done' | 'pending' | 'blocked';

export interface NodeColors {
    background: string;
    border: string;
    text: string;
}

// Color badges for live progress tracking (optional) — shadcn-style zinc palette.
// Same palette as the drawer badges / dialog legend (location-tracker.css) so a status
// reads the same everywhere: black = active, green = complete, red = on hold, gray = pending.
export const PROGRESS_COLORS: Record<WorkCenterProgressStatus, NodeColors> = {
    current: { background: '#18181b', border: '#18181b', text: '#FAFAFA' }, // Black: currently active
    done: { background: '#E3F2EA', border: '#2F855A', text: '#2F855A' }, // Green: completed
    pending: { background: '#F4F4F5', border: '#E4E4E7', text: '#A1A1AA' }, // Muted gray: upcoming
    blocked: { background: '#FBE9E9', border: '#D64545', text: '#D64545' }, // Red: active work parked by a production floor hold
};

const DEFAULT_COLORS: NodeColors = { background: '#FFFFFF', border: '#C9D1DB', text: '#1F2933' };

// -------------------------------------------------------------
// 2. DATA STRUCTURES
// -------------------------------------------------------------
interface DiagramNode {
    id: string;
    x: number;
    y: number;
    width: number;      // stage-wide box width — grows with the longest code of the stage
    code: string;       // display code, possibly truncated with "…" (e.g. "ALUCUT")
    fullCode: string;   // untruncated code — shown in the hover tooltip
    name: string;       // work center full name — shown in the hover tooltip
    position: string;   // e.g. "2.1.1", or a caller-supplied sub-label, possibly truncated with "…"
    fullPosition: string; // untruncated sub-label — shown in the hover tooltip when truncated
    positionLines: string[]; // sub-label split into the lines drawn under the code
    colors: NodeColors;
    workCenterId: string;
    clickable: boolean;
    selected: boolean;
    locked: boolean;
}

interface DiagramConnector {
    key: string;
    type: 'straight' | 'curve';
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    curvePath?: string; // SVG path data (d) for smooth curves
}

interface Point {
    x: number;
    y: number;
}

/** accent for clickable nodes: black for a forward move, red for a revert */
export type ClickableTone = 'default' | 'danger';

interface RouteFlowDiagramProps {
    stages: WorkflowStageEntity[];
    statusByWorkCenterId?: Record<string, WorkCenterProgressStatus>;
    height?: number;
    clickableWorkCenterIds?: string[];
    clickableTone?: ClickableTone;
    /** clickable nodes the user has picked — drawn filled with the accent colour */
    selectedWorkCenterIds?: string[];
    /** would-be targets the user has no work center access to — drawn faded, not clickable */
    lockedWorkCenterIds?: string[];
    /** replaces the x.x.x position text under each code; a work center without an entry shows no sub-label */
    subLabelByWorkCenterId?: Record<string, string>;
    /** 'scale' (default) shrinks the whole diagram to the container; 'scroll' keeps it at natural size and scrolls sideways */
    fit?: 'scale' | 'scroll';
    /** 'default' (compact) or 'large' — bigger boxes and text for the floor-facing tracking view */
    size?: DiagramSize;
    onWorkCenterClick?: (workCenterId: string) => void;
}

// -------------------------------------------------------------
// 3. LAYOUT CALCULATOR (Turns stages data into coordinates)
// -------------------------------------------------------------
function calculateDiagramLayout(
    stages: WorkflowStageEntity[],
    statusByWorkCenterId?: Record<string, WorkCenterProgressStatus>,
    clickableWorkCenterIds?: string[],
    clickableTone: ClickableTone = 'default',
    selectedWorkCenterIds?: string[],
    lockedWorkCenterIds?: string[],
    subLabelByWorkCenterId?: Record<string, string>,
    dims: DiagramDimensions = DIAGRAM_SIZES.default
) {
    const nodes: DiagramNode[] = [];
    const connectors: DiagramConnector[] = [];

    if (!stages || stages.length === 0) {
        return { nodes, connectors, totalWidth: 0, totalHeight: 0, maxLanes: 1 };
    }

    // Maximum number of parallel legs across any stage (determines vertical height)
    const maxLanes = Math.max(1, ...stages.map((stage) => stage.entries.length));

    let cursorX = dims.padding;
    let previousExits: Point[] = [];

    stages.forEach((stage, stageIndex) => {
        const isParallelStage = stage.entries.length > 1;

        // Longest leg in this stage (e.g. 3 steps)
        const maxStepsInStage = Math.max(1, ...stage.entries.map((e) => (e.steps?.length || 1)));

        // The widest code in this stage decides the stage's box width, so a
        // long work center code (e.g. "JT-Web-Development") never spills out
        // of its box; codes wider than the size's maxBoxWidth get truncated with "…"
        const stageCodes: string[] = stage.entries.flatMap((e) => (e.steps || []).map((step) => step.workCenterCode || '?'));
        const stageSubLabels: string[] = subLabelByWorkCenterId
            ? stage.entries.flatMap((e) => (e.steps || []).flatMap((step) => (subLabelByWorkCenterId[step.workCenterId] || '').split('\n')))
            : [];
        const stageBoxWidth = Math.min(
            dims.maxBoxWidth,
            Math.max(
                dims.boxWidth,
                ...stageCodes.map((code) => code.length * dims.codeCharWidth + dims.textSidePadding),
                ...stageSubLabels.map((label) => label.length * dims.subLabelCharWidth + dims.textSidePadding)
            )
        );
        const maxCodeChars = Math.floor((stageBoxWidth - dims.textSidePadding) / dims.codeCharWidth);
        const maxSubLabelChars = Math.floor((stageBoxWidth - dims.textSidePadding) / dims.subLabelCharWidth);

        const stageEndX = cursorX + maxStepsInStage * (stageBoxWidth + dims.horizontalGap) - dims.horizontalGap;

        const currentEntries: Point[] = [];
        const currentExits: Point[] = [];

        stage.entries.forEach((entry, entryIndex) => {
            // `steps` (the flattened, enriched path) is always populated by both
            // callers now — the backend for saved routes, and the route builder's
            // own live preview for a route still being edited
            const steps = entry.steps || [];

            // Single leg is centered vertically; parallel legs sit on separate rows
            const laneY = stage.entries.length === 1
                ? dims.padding + ((maxLanes - 1) / 2) * dims.rowGap
                : dims.padding + entryIndex * dims.rowGap;
            const centerY = laneY + dims.boxHeight / 2;

            steps.forEach((step, stepIndex) => {
                const nodeX = cursorX + stepIndex * (stageBoxWidth + dims.horizontalGap);

                const fullCode = step.workCenterCode || '?';
                const displayCode = fullCode.length > maxCodeChars
                    ? `${fullCode.slice(0, Math.max(1, maxCodeChars - 1))}…`
                    : fullCode;

                // Label e.g. "1.0.0" for single, "2.1.3" for Stage 2, Leg 1, Step 3
                const positionText = isParallelStage
                    ? `${stageIndex + 1}.${entryIndex + 1}.${stepIndex + 1}`
                    : (steps.length > 1 ? `${stageIndex + 1}.1.${stepIndex + 1}` : `${stageIndex + 1}.0.0`);

                // a caller-supplied sub-label replaces the position; "\n" splits it into lines, each truncated like the code
                const fullSubLabel = subLabelByWorkCenterId ? (subLabelByWorkCenterId[step.workCenterId] || '') : positionText;
                const fullSubLabelLines = fullSubLabel.split('\n').filter((line) => line.trim() !== '');
                const subLabelLines = fullSubLabelLines.map((line) => line.length > maxSubLabelChars
                    ? `${line.slice(0, Math.max(1, maxSubLabelChars - 1))}…`
                    : line);

                // Color by status if status map is provided
                const status = statusByWorkCenterId?.[step.workCenterId];
                let nodeColors = status ? PROGRESS_COLORS[status] : DEFAULT_COLORS;

                const clickable = clickableWorkCenterIds ? clickableWorkCenterIds.includes(step.workCenterId) : false;
                const selected = clickable && selectedWorkCenterIds ? selectedWorkCenterIds.includes(step.workCenterId) : false;
                const locked = !clickable && lockedWorkCenterIds ? lockedWorkCenterIds.includes(step.workCenterId) : false;
                if (clickable) {
                    // an active step that is also a move target (finish / finish-track) gets its own amber
                    // accent — reusing black (the active color) would render it identically to a plain,
                    // non-actionable active step, and black-on-black nearly hides the dashed "clickable" ring
                    const isActiveTarget = status === 'current';
                    const accent = clickableTone === 'danger'
                        ? PROGRESS_COLORS.blocked.border
                        : isActiveTarget ? '#B45309' : '#18181b';
                    nodeColors = selected
                        ? { background: accent, border: accent, text: '#FAFAFA' }
                        : { background: isActiveTarget ? '#FFF8F0' : '#FFFFFF', border: accent, text: accent };
                }

                // 1. Add the work center box
                nodes.push({
                    id: `stage-${stageIndex}-leg-${entryIndex}-step-${stepIndex}`,
                    x: nodeX,
                    y: laneY,
                    width: stageBoxWidth,
                    code: displayCode,
                    fullCode,
                    name: step.workCenterName || '',
                    position: subLabelLines.join(' · '),
                    fullPosition: fullSubLabelLines.join(' · '),
                    positionLines: subLabelLines,
                    colors: nodeColors,
                    workCenterId: step.workCenterId,
                    clickable,
                    selected,
                    locked,
                });

                // 2. Connect steps within the SAME leg with straight lines (e.g. ALUCUT -> ASSEMBLY)
                if (stepIndex > 0) {
                    const prevBoxRight = nodeX - dims.horizontalGap;
                    connectors.push({
                        key: `intra-leg-${stageIndex}-${entryIndex}-${stepIndex}`,
                        type: 'straight',
                        x1: prevBoxRight,
                        y1: centerY,
                        x2: nodeX,
                        y2: centerY,
                    });
                } else {
                    currentEntries.push({ x: nodeX, y: centerY });
                }

                // 3. Track the exit point of this leg
                if (stepIndex === steps.length - 1) {
                    const legExitX = nodeX + stageBoxWidth;

                    // If this leg is shorter than other parallel legs (e.g. DHO has 1 step while ALUCUT has 3),
                    // extend its horizontal line across its row so it merges neatly at the end of the stage
                    if (legExitX < stageEndX) {
                        connectors.push({
                            key: `leg-extend-${stageIndex}-${entryIndex}`,
                            type: 'straight',
                            x1: legExitX,
                            y1: centerY,
                            x2: stageEndX,
                            y2: centerY,
                        });
                        currentExits.push({ x: stageEndX, y: centerY });
                    } else {
                        currentExits.push({ x: legExitX, y: centerY });
                    }
                }
            });
        });

        // 4. Connect previous stage's exits to this stage's entries
        if (previousExits.length > 0) {
            previousExits.forEach((fromPoint, fromIndex) => {
                currentEntries.forEach((toPoint, toIndex) => {
                    const key = `stage-link-${stageIndex}-${fromIndex}-${toIndex}`;

                    if (Math.abs(fromPoint.y - toPoint.y) < 1) {
                        // Same vertical row -> straight horizontal line
                        connectors.push({
                            key,
                            type: 'straight',
                            x1: fromPoint.x,
                            y1: fromPoint.y,
                            x2: toPoint.x,
                            y2: toPoint.y,
                        });
                    } else {
                        // Different rows -> smooth curve (curly brace { for split, } for merge)
                        const midX = (fromPoint.x + toPoint.x) / 2;
                        const curvePath = `M ${fromPoint.x} ${fromPoint.y} C ${midX} ${fromPoint.y}, ${midX} ${toPoint.y}, ${toPoint.x} ${toPoint.y}`;
                        connectors.push({
                            key,
                            type: 'curve',
                            x1: fromPoint.x,
                            y1: fromPoint.y,
                            x2: toPoint.x,
                            y2: toPoint.y,
                            curvePath,
                        });
                    }
                });
            });
        }

        previousExits = currentExits;
        cursorX = stageEndX + dims.horizontalGap;
    });

    const totalWidth = cursorX - dims.horizontalGap + dims.padding;
    const totalHeight = (maxLanes - 1) * dims.rowGap + dims.boxHeight + dims.padding * 2;

    return { nodes, connectors, totalWidth, totalHeight, maxLanes };
}

// -------------------------------------------------------------
// 4. MAIN COMPONENT (Renders standard SVG - works everywhere)
// -------------------------------------------------------------
function RouteFlowDiagram({ stages, statusByWorkCenterId, height, clickableWorkCenterIds, clickableTone = 'default', selectedWorkCenterIds, lockedWorkCenterIds, subLabelByWorkCenterId, fit = 'scale', size = 'default', onWorkCenterClick }: RouteFlowDiagramProps) {
    const dims = DIAGRAM_SIZES[size];

    const { nodes, connectors, totalWidth, totalHeight, maxLanes } = useMemo(
        () => calculateDiagramLayout(stages, statusByWorkCenterId, clickableWorkCenterIds, clickableTone, selectedWorkCenterIds, lockedWorkCenterIds, subLabelByWorkCenterId, dims),
        [stages, statusByWorkCenterId, clickableWorkCenterIds, clickableTone, selectedWorkCenterIds, lockedWorkCenterIds, subLabelByWorkCenterId, dims]
    );

    // hover tooltip lives outside the (possibly scrolling) canvas so it is never clipped by it
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [hoverTip, setHoverTip] = useState<{ left: number; top: number; title: string; detail: string } | null>(null);

    const showTip = (event: React.MouseEvent<SVGGElement>, node: DiagramNode): void => {
        if (!wrapperRef.current) { return; }
        const wrapRect = wrapperRef.current.getBoundingClientRect();
        const nodeRect = event.currentTarget.getBoundingClientRect();
        setHoverTip({
            left: nodeRect.left - wrapRect.left + nodeRect.width / 2,
            top: nodeRect.top - wrapRect.top,
            title: node.name && node.name !== node.fullCode ? `${node.fullCode} · ${node.name}` : node.fullCode,
            detail: node.locked ? 'No access at the current work center to move here' : node.fullPosition,
        });
    };

    if (nodes.length === 0) return null;

    // Outer container height
    const containerHeight = height || Math.max(100, (maxLanes - 1) * dims.rowGap + dims.boxHeight + dims.padding * 2 + 20);

    // the code and its sub-label lines are packed as one block centred in the fixed-height box
    // (default size: one line sits on the 21 / 33 baselines, two lines tighten to 15.5 / 27.5 / 38.5);
    // the code line is ~codeFontSize tall with its baseline ~0.8 of that down, or ~0.36 above centre when alone
    const codeBaseline = (lineCount: number): number => lineCount > 0
        ? (dims.boxHeight - (dims.codeFontSize + 0.5 + lineCount * dims.subLabelLineHeight)) / 2 + dims.codeFontSize * 0.8
        : dims.boxHeight / 2 + dims.codeFontSize * 0.36;

    const isScroll = fit === 'scroll';

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div
                style={{
                    width: '100%',
                    height: isScroll ? 'auto' : containerHeight,
                    borderRadius: 12,
                    overflowX: isScroll ? 'auto' : 'hidden',
                    overflowY: 'hidden',
                    background: '#FBFCFE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isScroll ? 'flex-start' : 'center',
                    padding: '4px 8px',
                    boxSizing: 'border-box',
                }}
                onScroll={() => setHoverTip(null)}
            >
                <svg
                    viewBox={`0 0 ${totalWidth} ${totalHeight}`}
                    width={isScroll ? totalWidth : '100%'}
                    height={isScroll ? totalHeight : '100%'}
                    preserveAspectRatio="xMidYMid meet"
                    // auto margins center a narrow diagram and collapse to 0 once it overflows, so scrolling starts at the first stage
                    style={isScroll ? { display: 'block', flex: '0 0 auto', margin: '0 auto' } : { display: 'block', maxWidth: '100%', maxHeight: '100%' }}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {/* Subtle box shadow for depth */}
                    <defs>
                        <filter id="box-shadow" x="-8%" y="-15%" width="120%" height="140%">
                            <feDropShadow dx="0" dy="1.2" stdDeviation="1.5" floodColor="#1F2933" floodOpacity="0.06" />
                        </filter>
                    </defs>

                    {/* 1. All connecting lines and curves */}
                    <g id="connectors">
                        {connectors.map((c) => (
                            c.type === 'curve' && c.curvePath ? (
                                <path
                                    key={c.key}
                                    d={c.curvePath}
                                    fill="none"
                                    stroke={LINE_COLOR}
                                    strokeWidth={1.8}
                                    strokeLinecap="round"
                                />
                            ) : (
                                <line
                                    key={c.key}
                                    x1={c.x1}
                                    y1={c.y1}
                                    x2={c.x2}
                                    y2={c.y2}
                                    stroke={LINE_COLOR}
                                    strokeWidth={1.8}
                                    strokeLinecap="round"
                                />
                            )
                        ))}
                    </g>

                    {/* 2. All work center boxes */}
                    <g id="boxes">
                        {nodes.map((node) => (
                            <g
                                key={node.id}
                                style={{ cursor: node.clickable ? 'pointer' : node.locked ? 'not-allowed' : 'default' }}
                                opacity={node.locked ? 0.45 : 1}
                                onClick={node.clickable && onWorkCenterClick ? () => onWorkCenterClick(node.workCenterId) : undefined}
                                onMouseEnter={(event) => showTip(event, node)}
                                onMouseLeave={() => setHoverTip(null)}
                            >
                                {/* Card box */}
                                <rect
                                    x={node.x}
                                    y={node.y}
                                    width={node.width}
                                    height={dims.boxHeight}
                                    rx={dims.cornerRadius}
                                    fill={node.colors.background}
                                    stroke={node.colors.border}
                                    strokeWidth={node.clickable ? 2.2 : 1.6}
                                    strokeDasharray={node.clickable && !node.selected ? '6 4' : undefined}
                                    filter="url(#box-shadow)"
                                />
                                {/* Work center code (e.g. "ALUCUT") — sits on the upper line, or centered when there is no sub-label */}
                                <text
                                    x={node.x + node.width / 2}
                                    y={node.y + codeBaseline(node.positionLines.length)}
                                    textAnchor="middle"
                                    fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                                    fontSize={dims.codeFontSize}
                                    fontWeight={600}
                                    fill={node.colors.text}
                                >
                                    {node.code}
                                </text>
                                {/* Sub-label lines: the route's own position text (e.g. "2.1.1") keeps its weight; caller labels (date, name) read as lighter detail */}
                                {node.positionLines.map((line, lineIndex) => (
                                    <text
                                        key={lineIndex}
                                        x={node.x + node.width / 2}
                                        y={node.y + codeBaseline(node.positionLines.length) + (dims.subLabelLineHeight + 1) + lineIndex * dims.subLabelLineHeight}
                                        textAnchor="middle"
                                        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                                        fontSize={subLabelByWorkCenterId ? dims.subLabelFontSize : dims.positionFontSize}
                                        fontWeight={subLabelByWorkCenterId ? 400 : 600}
                                        fill={node.colors.text}
                                        opacity={subLabelByWorkCenterId ? dims.subLabelOpacity : 0.7}
                                    >
                                        {line}
                                    </text>
                                ))}
                            </g>
                        ))}
                    </g>
                </svg>
            </div>
            {hoverTip && (
                <div
                    style={{
                        position: 'absolute',
                        left: hoverTip.left,
                        top: hoverTip.top,
                        transform: 'translate(-50%, calc(-100% - 8px))',
                        padding: '6px 10px',
                        borderRadius: 6,
                        background: '#18181b',
                        color: '#FAFAFA',
                        fontSize: 11.5,
                        lineHeight: 1.45,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        boxShadow: '0 4px 12px rgba(31, 41, 51, 0.18)',
                        zIndex: 5,
                    }}
                >
                    <div style={{ fontWeight: 600 }}>{hoverTip.title}</div>
                    {hoverTip.detail && <div style={{ opacity: 0.8 }}>{hoverTip.detail}</div>}
                </div>
            )}
        </div>
    );
}

export default RouteFlowDiagram;
